import cn from "classnames";
import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { humanizedSecondsRange } from "helpers/date";
import { hiddenUserData } from "helpers/hiddenData";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import sum from "lodash/sum";
import sumBy from "lodash/sumBy";
import moment from "moment";
import React, { useMemo, useState } from "react";
import CommonReport from "./commonReport";

const calculateFilterOptions = (data) => {
    const uniqKeys = {
        counterparty: {},
        federalDistrict: {},
        state: {},
        city: {},
        driver: {},
        car: {},
    };
    return Object.keys(uniqKeys).reduce(
        (acc, key) => {
            acc[key] = data.reduce((options, item) => {
                if (uniqKeys[key][item[key]]) {
                    return options;
                }
                uniqKeys[key][item[key]] = true;
                options.push({ label: item[key], value: item[key] });
                return options;
            }, []);
            return acc;
        },
        {
            counterparty: [],
            federalDistrict: [],
            state: [],
            city: [],
            driver: [],
            car: [],
        },
    );
};

const TimeTotalFooter = ({ rows, column, isGroupedByCounterpartyReport }) => {
    const total = useMemo(
        () =>
            rows.reduce((sum, row) => {
                if (isGroupedByCounterpartyReport) {
                    if (!row.isGrouped) {
                        return sum;
                    }
                    return (
                        sum +
                        sumBy(row.leafRows, (item) => moment.duration(item.values[columnName(column)]).asSeconds())
                    );
                }

                return sum + moment.duration(row.values[columnName(column)]).asSeconds();
            }, 0),
        [column, isGroupedByCounterpartyReport, rows],
    );

    return total ? <strong>{humanizedSecondsRange(total)}</strong> : null;
};

const FlatRowsTotalFooter = ({ rows, column }) => {
    const total = useMemo(
        () => rows.reduce((acc, item) => acc + (item.isGrouped ? 0 : item.values[columnName(column)]), 0),
        [column, rows],
    );

    return <strong>{total}</strong>;
};

const VisitsTotalFooter = ({ rows }) => {
    const total = useMemo(() => sumBy(rows, (item) => item.leafRows?.length || 0), [rows]);

    return total ? <strong>{total}</strong> : null;
};

const BidsTotalFooter = ({ rows }) => {
    const total = useMemo(() => {
        const items = new Set();
        rows.forEach((el) => {
            if (!el.isGrouped) {
                items.add(el.original.bidId);
            }
        });
        return items.size;
    }, [rows]);

    return total ? <strong>{total}</strong> : null;
};

class LoadUnloadTimeReport extends CommonReport {
    constructor(props) {
        super(props);
        this.state.filterOptions = calculateFilterOptions(props.data.data);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.dataLoadedAt !== this.props.dataLoadedAt) {
            this.setState({ filterOptions: calculateFilterOptions(nextProps.data.data) });
        }
    }

    defaultColumnProps = (column) => {
        switch (column.propertyName) {
            case "CounterpartyId":
            case "FederalDistrict":
            case "State":
            case "City":
            case "Car":
            case "Driver": {
                const key = column.propertyName === "CounterpartyId" ? "counterparty" : columnName(column);
                return {
                    Filter: reportFilterWrapper((props) => (
                        <SelectColumnFilter
                            {...props}
                            selectProps={{
                                ...defaultMultipleSelectProps,
                                options: this.state.filterOptions[key],
                                inputProps: { name: key },
                                components: { ValueContainer },
                            }}
                        />
                    )),
                    filter: (rows, _id, filterValue) => {
                        if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                            return rows;
                        }
                        return rows.filter((row) => filterValue.value.includes(row.values[key]));
                    },
                };
            }
            default:
                return typeSpecificProps(column);
        }
    };

    cellProps = (column) => {
        return {
            Header: column.label,
            accessor: columnName(column),
            sticky: column.propertyName === "CounterpartyId",
            Cell: (props) => {
                const { id } = props.column;
                const { counterparty, city } = props.row.values;
                const value = counterparty ? counterparty + city : city;
                const uniqueId = { [`data-${props.column.id}`.toLowerCase()]: value };
                switch (id) {
                    case "counterpartyId": {
                        const { subRows } = props.row;
                        const counterpartyName = subRows[0]?.original?.counterparty || "Без контрагента";
                        return (
                            <div data-counterparty-id={props.value} className="ellipsis">
                                {counterpartyName}
                            </div>
                        );
                    }
                    case "bidId": {
                        return (
                            <a
                                data-bidid={props.value}
                                href={paths.bidShow("bid", props.value)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {props.value}
                            </a>
                        );
                    }
                    case "client": {
                        return (
                            <div {...uniqueId} className="long-text">
                                {hiddenUserData(formatValue(props.value, column.clrType), { type: "title" })}
                            </div>
                        );
                    }
                    case "numberOfVisits":
                    case "countOfIdlesAtLoad":
                    case "countOfIdlesAtUnload":
                    case "countOfIdlesAtLoadUnload": {
                        return null;
                    }
                    default: {
                        return (
                            <div {...uniqueId} className="long-text">
                                {formatValue(props.value, column.clrType)}
                            </div>
                        );
                    }
                }
            },
            ...this.defaultColumnProps(column),
        };
    };

    extractColumns = () => {
        const { columns } = this.props.data || {};
        const reportColumns = columns.map((column) => {
            return {
                ...this.cellProps(column),
                aggregate: (leafValues) => {
                    switch (column.propertyName) {
                        case "BidId":
                            return new Set(leafValues).size;
                        case "NumberOfVisits":
                        case "CountOfIdlesAtLoad":
                        case "CountOfIdlesAtUnload":
                        case "CountOfIdlesAtLoadUnload":
                            return sum(leafValues) || 0;
                        case "LateLoadTime":
                        case "LateUnloadTime":
                        case "IdleLoadTime":
                        case "IdleUnloadTime":
                        case "IdleLoadUnloadTime":
                        case "AverageLoadTime":
                        case "AverageUnloadTime":
                        case "AverageLoadUnloadTime": {
                            const durationSum = sumBy(leafValues, (item) => moment.duration(item).asSeconds());
                            const count = leafValues.reduce((acc, item) => (item != null ? ++acc : acc), 0);
                            if (!count) {
                                return null;
                            }
                            return Math.round(durationSum / count);
                        }
                        default:
                            return null;
                    }
                },
                Aggregated: ({ value }) => {
                    switch (column.propertyName) {
                        case "LateLoadTime":
                        case "LateUnloadTime":
                        case "IdleLoadTime":
                        case "IdleUnloadTime":
                        case "IdleLoadUnloadTime":
                        case "AverageLoadTime":
                        case "AverageUnloadTime":
                        case "AverageLoadUnloadTime": {
                            return <strong>{value ? humanizedSecondsRange(value) : null}</strong>;
                        }
                        case "CountOfIdlesAtLoad":
                        case "CountOfIdlesAtUnload":
                        case "CountOfIdlesAtLoadUnload": {
                            return value ? <strong>{value}</strong> : null;
                        }
                        default:
                            return value !== null ? <strong>{formatValue(value, column.clrType)}</strong> : "";
                    }
                },
                Footer: (info) => {
                    switch (column.propertyName) {
                        case "CounterpartyId": {
                            const haveWithoutCounterpartyRow = info.rows.some((item) => item.groupByVal === "null");
                            return (
                                <strong>
                                    Всего ({haveWithoutCounterpartyRow ? info.rows.length - 1 : info.rows.length})
                                </strong>
                            );
                        }
                        case "LateLoadTime":
                        case "LateUnloadTime":
                        case "IdleLoadTime":
                        case "IdleUnloadTime":
                        case "IdleLoadUnloadTime":
                        case "AverageLoadTime":
                        case "AverageUnloadTime":
                        case "AverageLoadUnloadTime": {
                            return (
                                <TimeTotalFooter
                                    rows={info.rows}
                                    column={column}
                                    isGroupedByCounterpartyReport={this.props.isGroupedByCounterpartyReport}
                                />
                            );
                        }
                        case "CountOfIdlesAtLoad":
                        case "CountOfIdlesAtUnload":
                        case "CountOfIdlesAtLoadUnload": {
                            if (this.props.isGroupedByCounterpartyReport) {
                                return <FlatRowsTotalFooter rows={info.flatRows} column={column} />;
                            } else {
                                return null;
                            }
                        }
                        case "NumberOfVisits": {
                            if (this.props.isGroupedByCounterpartyReport) {
                                return <VisitsTotalFooter rows={info.rows} />;
                            } else {
                                return <strong>{info.rows.length}</strong>;
                            }
                        }
                        case "BidId": {
                            if (this.props.isGroupedByCounterpartyReport) {
                                return <BidsTotalFooter rows={info.flatRows} />;
                            } else {
                                return <strong>{info.rows.length}</strong>;
                            }
                        }
                        default:
                            return null;
                    }
                },
            };
        });
        if (!this.props.isGroupedByCounterpartyReport) {
            reportColumns.unshift(this.idColumn);
        }
        return reportColumns;
    };

    tableExtraProps = () => {
        return {
            initialState: {
                hiddenColumns: this.props.isGroupedByCounterpartyReport ? ["counterparty"] : [],
                groupBy: ["counterpartyId"],
            },
            className: cn("-striped mb-3 table-sm", {
                "r-item__load-unload": !this.props.isPrintForm,
            }),
        };
    };
}

const ConfiguredReport = (props) => {
    const [showInfoByEveryBid, setShowInfoByEveryBid] = useState(false);
    const [uniqKey, setUniqKey] = useState(false);

    const toggleCheckbox = () => {
        props.getItem({ showInfoByEveryBid: !showInfoByEveryBid }).then(() => setUniqKey(Date.now()));
        setShowInfoByEveryBid(!showInfoByEveryBid);
    };

    const checkboxCname = cn("checkbox d-inline-block mr-2", {
        "checkbox--checked": showInfoByEveryBid,
    });
    const isGroupedByCounterpartyReport = props.data.columns.some((column) => column.propertyName === "CounterpartyId");
    return (
        <>
            {!props.isPrintForm && !isGroupedByCounterpartyReport && (
                <div>
                    <div className="mb-2 btn" onClick={toggleCheckbox}>
                        <span className={checkboxCname} />
                        <small>Показывать данные по каждой заявке</small>
                    </div>
                </div>
            )}
            <LoadUnloadTimeReport
                key={uniqKey}
                {...props}
                isGroupedByCounterpartyReport={isGroupedByCounterpartyReport}
            />
        </>
    );
};

export default ConfiguredReport;
